import { useEffect, useState } from "react";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import type { Theme } from "../../lib/theme";
import { Nav } from "./Nav";
import { Footer } from "./Footer";
import {
  HomePage,
  PricingPage,
  HowItWorksPage,
  SecurityPage,
  PrivacyPage,
  TermsPage,
  ContactPage,
} from "./pages";
import { AuthPanel, type AuthMode } from "../auth/AuthPanel";
import { SIGNUPS_ENABLED } from "../../lib/flags";

/** Reset scroll to top whenever the route changes. */
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

export function Marketing({
  theme,
  onToggleTheme,
}: {
  theme: Theme;
  onToggleTheme: () => void;
}) {
  const [authMode, setAuthMode] = useState<AuthMode | null>(null);
  const navigate = useNavigate();
  const open = (m: AuthMode) => setAuthMode(m);

  // Private beta: public sign-ups are closed, so every "get started" CTA sends
  // people to the waitlist instead of the sign-up panel. Sign-in stays open for
  // existing accounts. Flip SIGNUPS_ENABLED to reopen.
  const getStarted = () =>
    SIGNUPS_ENABLED ? open("signUp") : navigate("/contact");

  return (
    <>
      <ScrollToTop />
      <Nav
        theme={theme}
        onToggleTheme={onToggleTheme}
        onSignIn={() => open("signIn")}
        onGetStarted={getStarted}
      />
      <main>
        <Routes>
          <Route path="/" element={<HomePage onGetStarted={getStarted} />} />
          <Route path="/pricing" element={<PricingPage onGetStarted={getStarted} />} />
          <Route path="/how-it-works" element={<HowItWorksPage />} />
          <Route path="/security" element={<SecurityPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <Footer />
      {authMode && (
        <AuthPanel initialMode={authMode} onClose={() => setAuthMode(null)} />
      )}
    </>
  );
}
